import { MediaTrackerFn } from '@/hooks/use-media-tracker/types';
import { useAudio } from '@rhg/hooks/use-audio';
import { useMediaTracker } from '@rhg/hooks/use-media-tracker';
import { MediaEvent } from '@rhg/utils/media';
import { useCallback, useEffect } from 'react';
import { AudioControls } from './controls';
import { AudioMinimalControls } from './minimal-controls';
import { AudioPlayerProps, AudioPlayerVariant } from './types';

export function AudioPlayer({
    audio,
    title,
    subtitle,
    image,
    customAction,
    controls,
    areControlsHidden,
    drawerTitleIcon,
    variant,
    autoplay,
    loop,
    previousProgress = 0,
    isInitiallyMuted,
    isFloating,
    willTrackProgress,
    onClose,
    trackFn,
    controlsSx,
    sx,
    onNextTrack,
    onPreviousTrack,
    onControlClick,
}: AudioPlayerProps) {
    const {
        loadAudio,
        mediaEl,
        isLoaded,
        controls: { setTime },
    } = useAudio();

    const trackerFn: MediaTrackerFn = useCallback(
        (progress) => {
            if (
                progress.eventType === MediaEvent.LoadedMetadata &&
                previousProgress
            ) {
                setTime(previousProgress / 1000);
            }
            trackFn?.(progress);
        },
        [previousProgress, setTime, trackFn]
    );

    useMediaTracker({
        assetType: 'audio',
        mediaEl,
        trackFn: trackerFn,
        willTrackProgress,
    });

    useEffect(() => {
        if (audio && !isLoaded) {
            loadAudio({
                autoplay,
                loop,
                audio,
                onClose,
                isInitiallyMuted,
            });
        }
    }, [audio, autoplay, isInitiallyMuted, isLoaded, loadAudio, loop, onClose]);

    // Varied controls
    return variant !== AudioPlayerVariant.Minimal ? (
        <AudioControls
            sx={sx}
            variant={variant}
            controls={controls}
            controlsSx={controlsSx}
            title={title}
            subtitle={subtitle}
            image={image}
            customAction={customAction}
            isFloating={isFloating}
            onStop={onClose}
            onNextTrack={onNextTrack}
            onPreviousTrack={onPreviousTrack}
            areControlsHidden={areControlsHidden}
            onControlClick={onControlClick}
            drawerTitleIcon={drawerTitleIcon}
        />
    ) : (
        <AudioMinimalControls
            sx={sx}
            controlsSx={controlsSx}
            title={title}
            subtitle={subtitle}
            image={image}
            isFloating={isFloating}
            onStop={onClose}
            controls={controls}
            areControlsHidden={areControlsHidden}
            onControlClick={onControlClick}
        />
    );
}
